import { Formatter } from '../formatter';

import { foreignPrefix1Digit, foreignPrefix2Digit, foreignPrefix3Digit, netNumbers3Digits } from './phone.constants';

/**
 * Format a phone number
 */
export const phoneFormatter: Formatter = {
    format(value: string): string {
        if (!value) {
            return value;
        }
        return value
            .replace(/^\+/, '00') // Replace + by 0
            .replace(/^0031/, '0') // Replace 0031 by 0
            .replace(/[^0-9]/g, ''); // Remove any non-numerical
    },
    formatDisplay(phoneNumber: string): string {
        const phoneNumberPrefixed = phoneNumber.replace(/^\+/, '00').replace(/[^\d]/g, '');

        if (phoneNumberPrefixed === '') {
            return phoneNumberPrefixed;
        }

        if (phoneNumberPrefixed.startsWith('06')) {
            return format(
                phoneNumberPrefixed,
                (nr) => nr.substring(0, 2),
                (nr) => addSpaces(nr.substring(2))
            );
        } else if (phoneNumberPrefixed.startsWith('00316')) {
            return format(
                phoneNumberPrefixed,
                (nr) => `0${nr.substring(4, 5)}`,
                (nr) => addSpaces(nr.substring(5))
            );
        } else if (phoneNumberPrefixed.startsWith('003106')) {
            return format(
                phoneNumberPrefixed,
                (nr) => `0${nr.substring(5, 6)}`,
                (nr) => addSpaces(nr.substring(6))
            );
        } else if (phoneNumberPrefixed.startsWith('0031') && netNumbers3Digits.includes(0 + phoneNumberPrefixed.substring(4, 6))) {
            return format(
                phoneNumberPrefixed,
                (nr) => `0${nr.substring(4, 6)}`,
                (nr) => addSpaces(nr.substring(6))
            );
        } else if (phoneNumberPrefixed.startsWith('0031')) {
            return format(
                phoneNumberPrefixed,
                (nr) => `0${nr.substring(4, 7)}`,
                (nr) => addSpaces(nr.substring(7))
            );
        } else if (phoneNumberPrefixed.startsWith('00')) {
            const i = getForeignPrefixLength(phoneNumberPrefixed);
            return format(
                phoneNumberPrefixed,
                (nr) => `+${nr.substring(2, i + 2)}`,
                (nr) => addSpaces(nr.substring(i + 2)),
                ' '
            );
        } else if (netNumbers3Digits.includes(phoneNumberPrefixed.substring(0, 3))) {
            return format(
                phoneNumberPrefixed,
                (nr) => nr.substring(0, 3),
                (nr) => addSpaces(nr.substring(3))
            );
        }
        return format(
            phoneNumberPrefixed,
            (nr) => nr.substring(0, 4),
            (nr) => addSpaces(nr.substring(4))
        );
    }
};

function format(value: string, firstPart: (value) => string, secondPart: (value) => string, join = ' - '): string {
    if (secondPart(value) !== '') {
        return `${firstPart(value)}${join}${secondPart(value)}`.trim();
    }
    return firstPart(value);
}

function getForeignPrefixLength(phone: string): number {
    let i: number;
    if (foreignPrefix1Digit.includes(phone.substring(2, 3))) {
        i = 1;
    } else if (foreignPrefix2Digit.includes(phone.substring(2, 4))) {
        i = 2;
    } else if (foreignPrefix3Digit.includes(phone.substring(2, 5))) {
        i = 3;
    } else {
        i = 4;
    }
    return i;
}

/**
 * @ngdoc method
 * @name addSpaces
 * @description
 * The addSpaces method will add spaces depending on whether the string is even or odd
 * For an odd length a space will be added after the first three characters and then after every 2
 * For even length a space will be added after every 2 characters
 */
function addSpaces(phone: string): string {
    let output = '';

    if (phone.length % 2 === 0) {
        // even number (+33 00 22 33 44 55)
        output = matchAndJoin(phone);
    } else {
        // uneven number (+33 002 23 34 45 56)
        const threeDigits = phone.substring(0, 3);
        let surplus = phone.substring(3);
        surplus = matchAndJoin(surplus);
        output = `${threeDigits} ${surplus}`;
    }
    return output;
}

function matchAndJoin(phone: string): string {
    const matchedPhone = phone.match(/\d{2}/g);
    if (matchedPhone == null) {
        return phone;
    }
    return matchedPhone.join(' ');
}
